/**
 * Document and campaign context — an adviser rather than a drafting machine.
 *
 * Given the copy plus its audience, objective, layout and what is already in the document, it
 * decides *whether* an image is the right response before deciding what one should look like,
 * and **can legitimately answer "no image"**. That outcome has to be real and reachable, not a
 * polite hedge: a tool that always recommends making something is not advice.
 *
 * Self-contained by design. Everything transient — whether it is open, the collected context,
 * the in-flight recommendation, the draft under review — is scrapped on approve or discard, so
 * none of it belongs in the page. The one thing the page cares about, an approved prompt,
 * leaves through `onPromptApproved`.
 *
 * The guardrail note on pasted copy is **informational only**: drafting a recommendation is
 * not generation, so sensitive copy must never stop somebody seeing one.
 */

import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";

import type { CampaignBriefResponse } from "@/api/queries";
import { useCampaignBrief } from "@/api/queries";
import {
  IMAGE_RECOMMENDATION_LABELS,
  INTENDED_LAYOUT_LABELS,
  INTENDED_LAYOUT_OPTIONS,
  type IntendedLayout,
} from "@/constants/generate";
import type { GuardrailConfig } from "@/guardrail/rules";
import { detectSensitiveTopics } from "@/guardrail/rules";

import { assistFailureMessage } from "./assistFailure";
import { PromptReviewCard } from "./PromptReviewCard";

const REPORT_PLACEHOLDER =
  "Paste a section of the report or comms copy: an executive summary, a chapter intro or a " +
  "few key paragraphs is plenty. The theme and tone are what matter, not the length.";

const FAILURE_MESSAGE = "Couldn't get a recommendation just now. Please try again.";

/** Plain text only; anything binary would arrive as mojibake, not content. */
const TXT_ACCEPT = ".txt,text/plain";

interface ContextFields {
  reportText: string;
  pageTitle: string;
  supportingMessage: string;
  audience: string;
  communicationObjective: string;
  existingImagesNote: string;
  intendedLayout: IntendedLayout;
}

const EMPTY_CONTEXT: ContextFields = {
  reportText: "",
  pageTitle: "",
  supportingMessage: "",
  audience: "",
  communicationObjective: "",
  existingImagesNote: "",
  intendedLayout: "unsure",
};

export interface CampaignContextPanelProps {
  onPromptApproved: (prompt: string) => void;
  disabled?: boolean;
  config: GuardrailConfig | undefined;
}

export function CampaignContextPanel({
  onPromptApproved,
  disabled = false,
  config,
}: CampaignContextPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [showMoreContext, setShowMoreContext] = useState(false);
  const [fields, setFields] = useState<ContextFields>(EMPTY_CONTEXT);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [result, setResult] = useState<CampaignBriefResponse | null>(null);
  /** `null` = no draft in play — only ever set when the recommendation is not "no image". */
  const [draft, setDraft] = useState<string | null>(null);

  const campaignBrief = useCampaignBrief();

  function set<K extends keyof ContextFields>(key: K, value: ContextFields[K]) {
    setFields((current) => ({ ...current, [key]: value }));
  }

  const rawMatch = useMemo(
    () => detectSensitiveTopics(fields.reportText, config),
    [fields.reportText, config],
  );

  const canSubmit =
    !campaignBrief.isPending &&
    !disabled &&
    (fields.reportText.trim().length > 0 || fields.pageTitle.trim().length > 0);

  function handleSubmit() {
    setResult(null);
    setDraft(null);
    campaignBrief.mutate(fields, {
      onSuccess: (data) => {
        setResult(data);
        setDraft(data.recommendation === "no-image" ? null : (data.draftPrompt ?? null));
      },
    });
  }

  /** Reads a `.txt` entirely in the browser — no upload route, no parsing library. */
  async function handleFile(file: File) {
    setFileError(null);
    try {
      const text = await file.text();
      set("reportText", text);
      setFileName(file.name);
      setResult(null);
      setDraft(null);
    } catch {
      setFileError("Couldn't read that file. Try pasting the text instead.");
    }
  }

  function closeAndReset() {
    setResult(null);
    setDraft(null);
    setOpen(false);
  }

  /** Back to the form, keeping everything typed — for "no image", and for reconsidering. */
  function reconsider() {
    setResult(null);
    setDraft(null);
  }

  if (!open) {
    return (
      <Stack spacing={0.75} sx={{ alignItems: "flex-start" }}>
        <Button
          variant="outlined"
          startIcon={<DescriptionOutlined />}
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          Get an image recommendation from context
        </Button>
        <Typography variant="caption" color="text.secondary">
          Give us the report copy, audience and objective — we&rsquo;ll say whether an image
          fits at all, and draft one if it does.
        </Typography>
      </Stack>
    );
  }

  return (
    <Card sx={{ p: 2.5, border: "1px solid rgba(51,105,255,0.3)" }}>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={2} sx={{ justifyContent: "space-between" }}>
          <Box>
            <Typography variant="overline" color="primary.dark">
              Document and campaign context
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              We&rsquo;ll recommend the kind of image this needs — including, sometimes, none
              at all — before drafting anything.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            disabled={campaignBrief.isPending}
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </Stack>

        {result === null ? (
          <>
            <TextField
              label="Report or comms copy"
              multiline
              minRows={7}
              placeholder={REPORT_PLACEHOLDER}
              helperText="An extract is fine: very long text is trimmed before it reaches the model."
              value={fields.reportText}
              disabled={campaignBrief.isPending}
              onChange={(event) => {
                set("reportText", event.target.value);
                if (fileName) setFileName(null);
              }}
            />

            <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileOutlined />}
                disabled={campaignBrief.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                Load a .txt file
              </Button>
              <Typography variant="caption" color="text.secondary" noWrap>
                {fileName ? `Loaded ${fileName}` : "Plain text only, .docx and .pdf coming later."}
              </Typography>
              <Box
                component="input"
                ref={fileInputRef}
                type="file"
                accept={TXT_ACCEPT}
                sx={{ display: "none" }}
                disabled={campaignBrief.isPending}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const file = event.target.files?.[0];
                  if (file) void handleFile(file);
                  // Reset so re-picking the same file still fires onChange.
                  event.target.value = "";
                }}
              />
            </Stack>

            <Box>
              <Button size="small" variant="text" onClick={() => setShowMoreContext((c) => !c)} sx={{ px: 0 }}>
                {showMoreContext
                  ? "Hide extra context"
                  : "Add page title, audience and objective (optional)"}
              </Button>
              <Collapse in={showMoreContext} unmountOnExit>
                <Stack spacing={2} sx={{ mt: 1.5, p: 2, backgroundColor: "rgba(255,255,255,0.6)" }}>
                  <Box className="grid gap-4 sm:grid-cols-2">
                    <TextField
                      label="Page title"
                      value={fields.pageTitle}
                      disabled={campaignBrief.isPending}
                      onChange={(event) => set("pageTitle", event.target.value)}
                    />
                    <TextField
                      label="Audience"
                      helperText={'e.g. "Investors", "Employees".'}
                      value={fields.audience}
                      disabled={campaignBrief.isPending}
                      onChange={(event) => set("audience", event.target.value)}
                    />
                  </Box>
                  <TextField
                    label="Supporting message"
                    value={fields.supportingMessage}
                    disabled={campaignBrief.isPending}
                    onChange={(event) => set("supportingMessage", event.target.value)}
                  />
                  <TextField
                    label="Communication objective"
                    helperText={'e.g. "Build trust", "Announce a milestone".'}
                    value={fields.communicationObjective}
                    disabled={campaignBrief.isPending}
                    onChange={(event) => set("communicationObjective", event.target.value)}
                  />
                  <TextField
                    label="Existing images already in this document"
                    helperText="Helps the recommendation account for what's already there, not just what's new."
                    value={fields.existingImagesNote}
                    disabled={campaignBrief.isPending}
                    onChange={(event) => set("existingImagesNote", event.target.value)}
                  />
                  <TextField
                    select
                    label="Intended layout"
                    value={fields.intendedLayout}
                    disabled={campaignBrief.isPending}
                    onChange={(event) => set("intendedLayout", event.target.value as IntendedLayout)}
                  >
                    {INTENDED_LAYOUT_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {INTENDED_LAYOUT_LABELS[option]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Collapse>
            </Box>

            {rawMatch.flagged ? (
              <Alert severity="info" variant="outlined" icon={<InfoOutlined />}>
                <Typography variant="caption" sx={{ display: "block" }}>
                  This copy touches sensitive topics. That doesn&rsquo;t stop a recommendation
                  or a draft — you&rsquo;ll be asked to confirm any image is illustrative
                  before anything is generated.
                </Typography>
                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mt: 1 }}>
                  {rawMatch.matchedCategories.map((category) => (
                    <Chip key={category} size="small" label={category} />
                  ))}
                </Stack>
              </Alert>
            ) : null}

            {fileError ? (
              <Alert severity="warning" role="alert">
                {fileError}
              </Alert>
            ) : null}
            {campaignBrief.isError ? (
              <Alert severity="error" role="alert">
                {assistFailureMessage(campaignBrief.error, FAILURE_MESSAGE)}
              </Alert>
            ) : null}

            <Box>
              <Button
                variant="contained"
                loading={campaignBrief.isPending}
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                Get a recommendation
              </Button>
            </Box>
          </>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
              <Chip
                size="small"
                color={result.recommendation === "no-image" ? "warning" : "default"}
                label={IMAGE_RECOMMENDATION_LABELS[result.recommendation]}
              />
              {result.suggestedRatio ? (
                <Typography variant="caption" color="text.secondary">
                  Suggested shape: {result.suggestedRatio}
                </Typography>
              ) : null}
            </Stack>
            <Typography variant="body2">{result.reasoning}</Typography>

            {result.recommendation === "no-image" ? (
              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                <Button size="small" variant="outlined" onClick={reconsider}>
                  Adjust context and try again
                </Button>
                <Button size="small" variant="text" onClick={closeAndReset}>
                  Close
                </Button>
              </Stack>
            ) : draft !== null ? (
              <PromptReviewCard
                label="Drafted prompt"
                draft={draft}
                onDraftChange={setDraft}
                loading={campaignBrief.isPending}
                config={config}
                onApprove={() => {
                  onPromptApproved(draft.trim());
                  closeAndReset();
                }}
                onDiscard={reconsider}
              />
            ) : null}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
